'use client'

import { useState, useMemo } from 'react'
import { TaskRow } from './row'
import { usePeopleData } from '@/entries'
import { useTopics } from '@/topics'
import { useFeedMutations } from '@/feed'
import type { EntryWithPeople, Topic } from '@/document'
import type { TaskEntry } from './meta'
import type { Linked } from '../types'

interface UpcomingTimelineProps {
  overdue: EntryWithPeople[]
  today: EntryWithPeople[]
  nextWeek: EntryWithPeople[]
  thisMonth: EntryWithPeople[]
  later: EntryWithPeople[]
}

interface TimeSection {
  id: string
  title: string
  tasks: EntryWithPeople[]
  context: 'upcoming-today' | 'upcoming-other'
}

interface TopicGroup {
  topicId: string | null
  topicName: string
  tasks: EntryWithPeople[]
}

export function UpcomingTimeline({
  overdue,
  today,
  nextWeek,
  thisMonth,
  later,
}: UpcomingTimelineProps) {
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null)

  const { data: peopleData = [] } = usePeopleData()
  const { topics: allTopics = [] } = useTopics()
  const taskTopics = useMemo(
    () => allTopics.filter((t) => t.entryType === 'task'),
    [allTopics],
  )

  const people = useMemo(
    () => peopleData.map((person) => ({ id: person.id, name: person.title })),
    [peopleData],
  )

  const {
    handleCompleteTask,
    handleDeleteTask,
    handleSaveTask,
    handleChecklistToggle,
    getChecklistItems,
    completingId,
    deletingId,
    savingId,
  } = useFeedMutations()

  // Three simplified sections: Today (overdue+today), This Week, Later (thisMonth+later)
  const sections: TimeSection[] = useMemo(
    () => [
      {
        id: 'today',
        title: 'Today',
        tasks: [...overdue, ...today],
        context: 'upcoming-today' as const,
      },
      {
        id: 'thisWeek',
        title: 'This Week',
        tasks: nextWeek,
        context: 'upcoming-other' as const,
      },
      {
        id: 'later',
        title: 'Later',
        tasks: [...thisMonth, ...later],
        context: 'upcoming-other' as const,
      },
    ],
    [overdue, today, nextWeek, thisMonth, later],
  )

  // Group "Later" tasks by topic, sorted alphabetically
  const laterTopicGroups: TopicGroup[] = useMemo(() => {
    const laterSection = sections.find((s) => s.id === 'later')
    if (!laterSection || laterSection.tasks.length === 0) return []

    const topicMap = new Map<string, Topic>()
    for (const t of taskTopics) topicMap.set(t.id, t)

    const groups = new Map<string | null, EntryWithPeople[]>()

    for (const task of laterSection.tasks) {
      const ids = task.topicIds?.filter((id) => topicMap.has(id)) ?? []
      if (ids.length === 0) {
        const list = groups.get(null) ?? []
        list.push(task)
        groups.set(null, list)
      } else {
        for (const id of ids) {
          const list = groups.get(id) ?? []
          list.push(task)
          groups.set(id, list)
        }
      }
    }

    const result: TopicGroup[] = []
    for (const [topicId, tasks] of groups) {
      if (topicId === null) continue
      result.push({
        topicId,
        topicName: topicMap.get(topicId)!.name,
        tasks,
      })
    }
    result.sort((a, b) => b.tasks.length - a.tasks.length)

    // Add uncategorized at the end
    const uncategorized = groups.get(null)
    if (uncategorized) {
      result.push({
        topicId: null,
        topicName: 'Uncategorized',
        tasks: uncategorized,
      })
    }

    return result
  }, [sections, taskTopics])

  return (
    <div className="space-y-5">
      {sections.map((section) => {
        return (
          <section
            key={section.id}
            className={section.id === 'later' ? 'mt-6 space-y-1' : 'space-y-1'}
          >
            <div className="flex items-center gap-2 px-2">
              <h2 className="text-muted-foreground text-xs font-semibold tracking-widest uppercase">
                {section.title}
              </h2>
              {section.tasks.length > 0 && (
                <span className="text-muted-foreground/50 text-xs">
                  {section.tasks.length}
                </span>
              )}
            </div>

            {section.tasks.length === 0 ? (
              <p className="text-muted-foreground/50 px-2 py-2 text-sm">
                Nothing scheduled
              </p>
            ) : section.id === 'later' && laterTopicGroups.length > 0 ? (
              <div className="space-y-5">
                {laterTopicGroups.map((group) => (
                  <div key={group.topicId ?? '_uncategorized'}>
                    <h3 className="text-muted-foreground px-2 text-sm font-semibold">
                      {group.topicName}
                      <span className="text-muted-foreground/40 ml-1.5 text-xs font-normal">
                        {group.tasks.length}
                      </span>
                    </h3>
                    <div className="[&>div>div:first-child]:!items-center [&>div>div:first-child]:!py-1 [&>div>div:first-child>button:first-child]:!mt-0">
                      {group.tasks.map((task) => {
                        return (
                          <TaskRow
                            key={task.id}
                            entry={task as Linked<TaskEntry>}
                            context={section.context}
                            isExpanded={expandedRowId === task.id}
                            onToggle={() =>
                              setExpandedRowId(
                                expandedRowId === task.id ? null : task.id,
                              )
                            }
                            onDelete={() => handleDeleteTask(task.id)}
                            onSave={(updates) =>
                              handleSaveTask(task.id, updates)
                            }
                            isDeleting={deletingId === task.id}
                            isSaving={savingId === task.id}
                            hideTopics
                          />
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-0">
                {section.tasks.map((task) => {
                  return (
                    <TaskRow
                      key={task.id}
                      entry={task as Linked<TaskEntry>}
                      context={section.context}
                      isExpanded={expandedRowId === task.id}
                      onToggle={() =>
                        setExpandedRowId(
                          expandedRowId === task.id ? null : task.id,
                        )
                      }
                      onDelete={() => handleDeleteTask(task.id)}
                      onSave={(updates) => handleSaveTask(task.id, updates)}
                      isDeleting={deletingId === task.id}
                      isSaving={savingId === task.id}
                    />
                  )
                })}
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}
